import { Link } from "react-router-dom";
import { Building2, Star } from "lucide-react";
import { EntityCard } from "./common/EntityCard";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader } from "./ui/card";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { CompanyRelationship } from "@/utils/companyRelationship";
import { CompanyRelationshipBadge } from "@/components/companies/CompanyRelationshipBadge";

export interface Company {
  id: string;
  name: string;
  logo_url?: string;
  assignee?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  vat_number?: string;
  website_url?: string;
  relationship?: CompanyRelationship;
  is_dummy_data?: boolean;
  jobCount?: number;
}

interface CompanyCardProps {
  company: Company;
  className?: string;
  disableLinks?: boolean;
  isSelected?: boolean;
  isFavorite?: boolean;
}

export const CompanyCard = ({ company, className, disableLinks = false, isSelected = false, isFavorite = false }: CompanyCardProps) => {
  const { t } = useTranslation();

  const formatAddress = (company: Company): string => {
    const parts = [
      company.street,
      company.postal_code,
      company.city,
      company.country
    ].filter(Boolean);
    
    return parts.join(", ");
  };

  const displayAddress = formatAddress(company);

  const TitleComponent = disableLinks ? 'div' : Link;
  const ViewButtonComponent = disableLinks ? 'div' : Link;

  return (
    <div className={cn(
      "relative flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-sm",
      "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
      isSelected && "ring-2 ring-blue-500 border-blue-500",
      className
    )}>
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex-shrink-0">
            {company.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.name} 
                className="w-12 h-12 rounded object-contain bg-white dark:bg-gray-800 border p-1"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div 
              className={cn(
                "w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center text-digger-blue",
                company.logo_url && "hidden"
              )}
            >
              <Building2 className="h-6 w-6" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <TitleComponent 
              to={disableLinks ? undefined : `/companies/${company.id}`} 
              className={cn(
                "hover:text-primary transition-colors",
                disableLinks && "cursor-default"
              )}
            >
              <h3 className="text-lg font-semibold truncate text-gray-900 dark:text-gray-100 flex items-center gap-2">
                {company.name}
                {isFavorite && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
              </h3>
            </TitleComponent>
            <div className="mt-1">
              <CompanyRelationshipBadge relationship={company.relationship} size="sm" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col p-4 pb-0">
        {displayAddress && (
          <div className="mb-2">
            <div className="flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">{displayAddress}</span>
            </div>
          </div>
        )}
        {/* Job count as pill and View button on the same line */}
        <div className="flex flex-row justify-between items-center pt-2 pb-4 mt-auto w-full">
          <div className="flex flex-row gap-2 flex-nowrap">
            <div
              className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold bg-blue-50/30 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400"
            >
              <span>{company.jobCount ?? 0}</span>
              <span className="ml-1">{company.jobCount === 1 ? t("companies.job") : t("companies.jobs")}</span>
            </div>
          </div>
        <ViewButtonComponent
          to={disableLinks ? undefined : `/companies/${company.id}`}
          className={cn(
              "inline-flex items-center justify-center px-3 py-0.5 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500",
            disableLinks && "cursor-default hover:bg-blue-500 dark:hover:bg-blue-500"
          )}
        >
          {t("contacts.view")}
        </ViewButtonComponent>
        </div>
      </div>
    </div>
  );
};
